<template>
  <div>
    <HgPageNotFound
      :imageUrl="imageUrl"
      :title="t('notFoundMessage.listingNotFoundTitle')"
      :pageTitle="t('notFoundMessage.listingNotFoundPageTitle')"
      :displayRentCta="isRentButtonVisible"
      :displayBuyCta="isBuyButtonVisible"
      :rentCta="t('notFoundMessage.listingNotFoundRentCta')"
      :description="t('notFoundMessage.listingNotFoundDescription')"
      :buyCta="t('notFoundMessage.listingNotFoundBuyCta')"
      :theme="platform"
    ></HgPageNotFound>
    <LazyListingRecommendations
      :class="$style.listingRecommendations"
      :listingIds="listingIdsForRecommendations"
      :recommendationsVersion="recommendationsVersion"
      :title="String(t('notFoundMessage.listingNotFoundRecommendationsTitle'))"
      :gtmPageType="GTMPageType.PAGE_NOT_FOUND"
      :apiBaseUrl="apiBaseUrl"
      :listingApiEndpoint="listingApiEndpoint"
      :nextButtonTitle="t('recommendations.nextButtonTitle')"
      :prevButtonTitle="t('recommendations.previousButtonTitle')"
      :language="locale"
      :listingPictureDefaultAltText="t('listingPictureDefaultAltText')"
      :recommendationsPriceOnRequestText="t('recommendations.priceOnRequest')"
      :shouldInjectAcrHeaders="shouldInjectAcrHeaders()"
      :getAcrHeaders="getAcrHeaders"
      :generatePdpRoute="getPdpRoute"
      :theme="platform"
    />
  </div>
</template>

<script lang="ts" setup>
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import HgPageNotFound from '@smg-real-estate/domus/HgPageNotFound.vue';
import { OfferType } from '@smg-real-estate/hg-rets-schema';
import {
  LazyListingRecommendations,
  RecommendationsVersion,
} from '@smg-real-estate/seeker-app-shared-components';
import { computed, onMounted } from 'vue';

import { trackErrorPageLoad } from '@/components/ListingNotFound/ListingNotFound.tracking';
import { getAcrHeaders, shouldInjectAcrHeaders } from '@/helpers/get-acr-headers';
import { GTMPageSubType, GTMPageType } from '@/helpers/GTM/GTMParameters';
import { getCurrentPlatformTheme, isPlatformIs24 } from '@/helpers/platformDetection';
import { getPdpRoute } from '@/helpers/Router/RouterHelper';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const EMPTY_BOX_URL = 'https://media.homegate.ch/vector-illustrations/empty-box.svg';
const MOVING_PLANNER_URL = 'https://media.homegate.ch/immoscout24/moving-planner.png';

const route = useRoute();
const { t, locale } = useI18n();

defineOptions({
  name: 'ListingNotFound',
});

const imageUrl = isPlatformIs24() ? MOVING_PLANNER_URL : EMPTY_BOX_URL;
const platform = getCurrentPlatformTheme();

const listingId = computed((): string => {
  const pathParts = route.path.split('/');
  return pathParts[pathParts.length - 1];
});

const listingIdsForRecommendations = computed((): string[] => {
  return [listingId.value];
});
const recommendationsVersion = computed((): RecommendationsVersion => {
  return RecommendationsVersion.v2;
});
const routeOfferType = computed(() => {
  return route.meta?.offerType;
});
const isBuyButtonVisible = computed((): boolean => {
  return routeOfferType.value === OfferType.Buy;
});
const isRentButtonVisible = computed((): boolean => {
  return routeOfferType.value === OfferType.Rent;
});
const apiBaseUrl = computed((): string => {
  return import.meta.env.VUE_API_BASE_URL;
});
const listingApiEndpoint = computed((): string => {
  return import.meta.env.VUE_APP_LISTING_API_ENDPOINT;
});

onMounted(() => {
  trackErrorPageLoad(
    locale.value as HgSupportedLanguage,
    GTMPageSubType.LISTING_NOT_FOUND,
    listingId.value,
  );
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

.listingRecommendations {
  margin-top: $space-200;
  margin-bottom: $space-200;
}

.adSlot {
  margin-right: -$space-100;
  margin-left: -$space-100;

  > div > div {
    max-width: 100%;
    margin-top: $space-150;
    margin-bottom: $space-150;
  }

  @media (min-width: $viewport-medium-min) {
    margin-right: auto;
    margin-left: auto;
  }
}
</style>
